import React, { useCallback, useMemo } from 'react';
import { useContext } from 'react';

import { get, isEmpty } from 'lodash';

import { MainContext, useTranslation } from '@ezbobdev/ui-framework';
import { Tools } from '@ezbobdev/ui-sdk';

import { useUtils } from '../../utils/useUtils';

export const useProtectedComponent = (props, widgetName) => {
  const { actions, sdkActions } = useContext(MainContext);
  const { t } = useTranslation();

  const { getConfig } = useUtils(props, widgetName);

  const product = Tools.getOption('product');

  const { data } = sdkActions.handlers.getResponses('users.getUserPermissions') ?? {};

  const scope = getConfig('scope');

  const { components, handlers } = getConfig('permissions', {});

  const tenantScopes = get(data, 'tenantData', {});
  const productScopes = get(data, product, {});

  const hasPermissionForHandler = useCallback(
    (userPermissions, handlerName) => {
      const handlerPermissions = get(handlers, handlerName);
      if (!handlerPermissions) return true;
      return handlerPermissions.some((permission) => userPermissions.includes(permission));
    },
    [handlers],
  );

  const renderComponent = useMemo(() => {
    return () => {
      if (isEmpty(productScopes) && isEmpty(tenantScopes)) {
        console.error('No product or tenant permissions found from API');
        return null;
      }

      if (!scope) {
        console.error('Missing UI component configuration scopes');
        return null;
      }

      const userPermissions = get({ ...productScopes, ...tenantScopes }, scope, []);
      const componentPermissions = get(components, 'root', []);
      const userHasPermissionsToView = componentPermissions.some((permission) => userPermissions.includes(permission));

      if (componentPermissions?.length && !userHasPermissionsToView) return null;

      const modifiedChildProps = { ...props.children.props };

      for (const key in modifiedChildProps) {
        if (typeof modifiedChildProps[key] === 'function') {
          if (!hasPermissionForHandler(userPermissions, key)) {
            modifiedChildProps[key] = () => {
              actions.view.setAlerts([{ message: t(`clientMessages.INSUFFICIENT_PERMISSIONS`), type: 'error' }]);
            };
          }
        }
      }

      const modifiedChild = React.cloneElement(props.children, modifiedChildProps);

      return modifiedChild;
    };
  }, [tenantScopes, productScopes, scope, components, actions.view, props.children]);

  return { renderComponent };
};
